import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../components/PageHeader';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { User, Mail, Lock, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

export function RegisterPage() {
    const navigate = useNavigate();
    const { register } = useAuth();
    
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleRegister = async (e) => {
        e.preventDefault();
        setError('');

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (password.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }

        setLoading(true);

        try {
            const user = await register(email, password, name);
            toast.success(`Welcome to Friend of Burston, ${user.name}!`);
            navigate('/member');
        } catch (err) {
            setError(err.response?.data?.detail || 'Registration failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="page-container" data-testid="register-page">
            <PageHeader title="Join Friend of Burston" showBack />
            
            <div className="px-4 py-6 max-w-md mx-auto">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="w-20 h-20 rounded-full bg-primary mx-auto flex items-center justify-center mb-4">
                        <UserPlus className="w-10 h-10 text-white" />
                    </div>
                    <h1 className="text-2xl font-bold text-foreground mb-2">Become a Member</h1>
                    <p className="text-muted-foreground">Join today and start earning rewards</p>
                </div>

                {/* Benefits */}
                <div className="card-base bg-primary/5 mb-6">
                    <h3 className="font-bold text-foreground mb-3">Member Benefits:</h3>
                    <ul className="space-y-2 text-muted-foreground">
                        <li className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-primary" />
                            Earn 2 points for every £1 spent
                        </li>
                        <li className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-primary" />
                            Exclusive member-only offers
                        </li>
                        <li className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-primary" />
                            Café stamp card rewards
                        </li>
                        <li className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-primary" />
                            Early access to events
                        </li>
                    </ul>
                </div>

                {/* Register Form */}
                <form onSubmit={handleRegister} className="space-y-4" data-testid="register-form">
                    {error && (
                        <div className="bg-destructive/10 text-destructive p-4 rounded-xl text-center font-medium" data-testid="register-error">
                            {error}
                        </div>
                    )}

                    <div className="space-y-2">
                        <Label htmlFor="name" className="text-base font-medium">Full Name</Label>
                        <div className="relative">
                            <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                            <Input
                                id="name"
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Your full name"
                                className="input-base pl-12"
                                required
                                data-testid="name-input"
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="email" className="text-base font-medium">Email Address</Label>
                        <div className="relative">
                            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                            <Input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="your@email.com"
                                className="input-base pl-12"
                                required
                                data-testid="email-input"
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="password" className="text-base font-medium">Password</Label>
                        <div className="relative">
                            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                            <Input
                                id="password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Create a password"
                                className="input-base pl-12"
                                required
                                data-testid="password-input"
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="confirmPassword" className="text-base font-medium">Confirm Password</Label>
                        <div className="relative">
                            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                            <Input
                                id="confirmPassword"
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Confirm your password"
                                className="input-base pl-12"
                                required
                                data-testid="confirm-password-input"
                            />
                        </div>
                    </div>

                    <Button
                        type="submit"
                        disabled={loading}
                        className="w-full h-14 rounded-full text-lg font-bold mt-6"
                        data-testid="register-submit-btn"
                    >
                        {loading ? 'Creating Account...' : 'Join Now'}
                    </Button>
                </form>

                {/* Login Link */}
                <div className="mt-8 text-center">
                    <p className="text-muted-foreground mb-3">Already a member?</p>
                    <Button
                        variant="outline"
                        onClick={() => navigate('/login')}
                        className="h-12 rounded-full px-8"
                        data-testid="login-link"
                    >
                        Sign In
                    </Button>
                </div>
            </div>
        </div>
    );
}

export default RegisterPage;
